/**
 * Expiry-countdown helpers.
 *
 * Two rules live here, both paid for by real defects:
 *
 * 1. ET, never the viewer's zone (CLAUDE.md). The old inline version built dates in local time,
 *    so a reader in Asia saw a different countdown than the desk did.
 * 2. A past expiration is not a negative countdown. Since 2026-09-15 an option settles at the
 *    first exit cycle AFTER its expiration session, on that session's official close, so between
 *    Friday's close and Monday 9:50 AM ET a position is legitimately open with an expired
 *    contract. It used to render a yellow "-1d left" badge, and nothing told the reader the
 *    contract had expired and was awaiting settlement.
 */

const ET = 'America/New_York';

/** Today's ET calendar date as a UTC-midnight epoch, so day differences are whole days. */
function etTodayUtc(now: Date): number {
  const [y, m, d] = now
    .toLocaleDateString('en-CA', { timeZone: ET })
    .split('-')
    .map((v) => parseInt(v, 10));
  return Date.UTC(y, m - 1, d);
}

/** ET calendar days until `expiration` (YYYY-MM-DD). Negative once the session has passed.
 *  Returns null when the date is unusable — callers must not invent a countdown. */
export function etDaysToExpiry(expiration: string | undefined | null, now: Date = new Date()): number | null {
  if (!expiration) return null;
  const parts = String(expiration).slice(0, 10).split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map((v) => parseInt(v, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const exp = Date.UTC(y, m - 1, d);
  if (Number.isNaN(exp)) return null;
  // Date.UTC rolls an impossible date forward ("2026-13-45" -> Feb 14, 2027): reject it rather
  // than invent a countdown (B-026).
  const back = new Date(exp);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) {
    return null;
  }
  return Math.round((exp - etTodayUtc(now)) / 86400000);
}

/** True when an OPTION position's expiration session has passed: it is awaiting settlement. */
export function isAwaitingSettlement(strategy: string | undefined, days: number | null): boolean {
  return strategy !== 'SHARES' && days !== null && days < 0;
}

/** True when an option is close to expiry and still trading (never true while settling). */
export function isExpiringSoon(strategy: string | undefined, days: number | null, within = 3): boolean {
  return strategy !== 'SHARES' && days !== null && days >= 0 && days <= within;
}
